/**
 * index.ts — theme entry point + step flow.
 *
 * Hydrates the catalog, then drives the steps: step 0 (intro) → optional
 * "Welcome back" recent-creations screen (only when the device has saved designs)
 * → step 1 "Choose your base" → step 2 "Design your piece" (+ in-place step 3).
 *
 * It also owns the favorites session state: the active build, whether we're editing
 * a known saved design (`editingFavoriteId`) and the "saved" baseline signature that
 * powers the leave-guard's dirty check.
 */

import { readConfiguratorData, loadCatalog, hydrateBuild, closuresForChain, toClosureSelection, toCharmProduct } from './data';
import {
  createBuild, configureBases, setBase, setChain, setClosure, setLength, addCharm,
  lengthBounds, defaultLength, buildSignature, ringCount, runningTotal, serializeBuild,
  type BaseKind, type BuildState,
} from './state';
import { mountConfigurator } from './canvas';
import { mountStep0 } from './step0';
import { mountStep1 } from './step1';
import { mountRecentCreations } from './stepRecent';
import { listFavorites, saveFavorite, removeFavorite, type SavedBuild } from './favorites';
import { capturePiece } from './snapshot';
import { addBuildToCart, cartBuildKey, uploadSnapshot } from './cart';
import { confirmLeave, toast } from './overlays';
import { installLeaveGuard } from './leaveGuard';

// The theme's cart-add handler silently swallows a 422 (e.g. a charm sold out once you
// already have one in the bag). Wrap fetch once to surface that as a toast — non-invasive,
// the original response still flows to the theme handler.
let cartAddWatchInstalled = false;
function installCartAddErrorToast(): void {
  if (cartAddWatchInstalled) return;
  cartAddWatchInstalled = true;
  const orig = window.fetch.bind(window);
  window.fetch = function (input: RequestInfo | URL, opts?: RequestInit): Promise<Response> {
    const p = orig(input, opts);
    const url = typeof input === 'string' ? input : (input instanceof Request ? input.url : String(input));
    if (url.indexOf('/cart/add') !== -1) {
      p.then(res => {
        if (res && res.status === 422) {
          res.clone().json()
            .then((d: { description?: string; message?: string }) => toast(d?.description || d?.message || 'Some charms are sold out and couldn’t be added.'))
            .catch(() => toast('Some charms are sold out and couldn’t be added.'));
        }
      }).catch(() => {});
    }
    return p;
  } as typeof window.fetch;
}

async function init(): Promise<void> {
  const root = document.querySelector<HTMLElement>('[data-configurator-root]');
  if (!root) return;
  const loading = root.querySelector<HTMLElement>('[data-configurator-loading]');

  const data = readConfiguratorData();
  if (!data || (!data.chains?.length && !data.charms?.length)) {
    if (loading) loading.textContent = 'Configurator data unavailable.';
    console.warn('[configurator] no data found in #configurator-data');
    return;
  }

  // Charms are paginated 50/page; page 1 is inline. Fetch the remaining pages in
  // parallel and merge so the catalog + its facets are built from the full set.
  const totalPages = data.charmsTotalPages || 1;
  if (totalPages > 1) {
    const pages = Array.from({ length: totalPages - 1 }, (_, i) => i + 2);
    const extra = await Promise.all(pages.map(p =>
      fetch(`${location.pathname}?page=${p}`)
        .then(r => (r.ok ? r.text() : ''))
        .then(html => (html ? readConfiguratorData(new DOMParser().parseFromString(html, 'text/html'))?.charms ?? [] : []))
        .catch(() => [])
    ));
    data.charms = (data.charms ?? []).concat(...extra);
  }

  const catalog = loadCatalog(data);

  // Section-authored base config (max charms / from price / label) is authoritative.
  configureBases(catalog.bases);

  installCartAddErrorToast();

  // ── Favorites session state ────────────────────────────────────────────────
  // `build` is the live design carried across steps. `editingFavoriteId` is set when
  // the build came from (or was saved as) a known favorite, so SAVE updates it in
  // place. `baselineSig` is the signature considered "saved"; dirty = current ≠ it.
  let build: BuildState = createBuild('bracelet');
  let editingFavoriteId: string | null = null;
  // PDP deep-link (?use=<productId>): a charm/chain to preload once a base is picked. One-shot.
  let pendingCharmId: string | null = null;
  let pendingChainId: string | null = null;
  let baselineSig = '';
  let bagSig = '';            // signature of the build at the last Add-to-bag (suppresses the leave prompt)
  let editCartGroupId: string | null = null; // editing a piece opened from the cart → "Update piece in bag"

  if (loading) loading.remove();

  const REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const FADE_MS = 260;

  // Cross-fade between steps: fade the current screen out, mount the next, fade it in.
  function swap(mountFn: () => void, animated: boolean): void {
    const host = root!;
    if (!animated || REDUCE) { mountFn(); return; }
    const cur = host.firstElementChild as HTMLElement | null;
    if (cur) { cur.style.transition = `opacity ${FADE_MS}ms ease`; cur.style.opacity = '0'; }
    window.setTimeout(() => {
      mountFn();
      const next = host.firstElementChild as HTMLElement | null;
      if (next) {
        next.style.opacity = '0';
        next.style.transition = `opacity ${FADE_MS}ms ease`;
        requestAnimationFrame(() => requestAnimationFrame(() => { next.style.opacity = '1'; }));
      }
    }, cur ? FADE_MS : 0);
  }

  // ── Favorites: dirty check + save ──────────────────────────────────────────
  // Unsaved changes worth prompting about: a known favorite diverged from its saved
  // state, OR a brand-new build with at least one charm on the piece.
  function isDirty(): boolean {
    const sig = buildSignature(build);
    if (sig === bagSig) return false; // already added to the cart, unchanged since
    return editingFavoriteId ? sig !== baselineSig : ringCount(build) >= 1;
  }

  // Prompt to save when leaving the editor for an earlier step (stepper / back arrow),
  // not just on page navigation. Proceeds on save/discard, stays on cancel.
  async function leaveEditor(proceed: () => void): Promise<void> {
    if (!isDirty()) { proceed(); return; }
    const choice = await confirmLeave();
    if (choice === 'save') { await saveCurrent(); proceed(); }
    else if (choice === 'discard') proceed();
  }

  /** Capture a thumbnail, then save or update the current build as a favorite. Guarded so a
   *  double-click (or both Save buttons) can't fire two saves/uploads at once. */
  let saving = false;
  async function saveCurrent(): Promise<void> {
    if (saving) return;
    saving = true;
    try {
      await runSaveCurrent();
    } finally {
      saving = false;
    }
  }
  async function runSaveCurrent(): Promise<void> {
    const signature = buildSignature(build);
    // Crisp preview WITHOUT bloating localStorage (which would evict older saves): capture
    // high-res, upload to Files and store the tiny CDN URL. If the upload isn't available
    // (e.g. app proxy missing), fall back to a small local data-URL thumb so saving still works.
    const hiRes = await capturePiece(catalog, build, 1000);
    let snapshot = hiRes;
    if (hiRes) {
      const url = await uploadSnapshot(hiRes, editingFavoriteId ?? signature);
      snapshot = url ?? await capturePiece(catalog, build, 360);
    }
    const baseLabel = catalog.bases.find(b => b.kind === build.base)?.label ?? build.base;
    const chainName = build.chain?.title || build.chain?.chainType || '';
    const n = ringCount(build);
    const subtitle = (chainName ? `${chainName} · ` : '') + `${n} charm${n === 1 ? '' : 's'}`;

    const { record, updated } = await saveFavorite({
      id: editingFavoriteId ?? undefined,
      signature,
      title: baseLabel,
      subtitle,
      price: runningTotal(build),
      snapshot,
      build: serializeBuild(build),
    });
    editingFavoriteId = record.id;   // now editing this record
    baselineSig = signature;         // clean again
    toast(updated ? 'Updated your saved piece' : 'Added piece to your saved creations');
  }

  // Leave-guard: custom modal on in-page nav, native dialog on hard close (see module).
  installLeaveGuard({
    isDirty,
    onLeaveIntent: async () => {
      const choice = await confirmLeave();
      if (choice === 'save') { await saveCurrent(); return true; }
      return choice === 'discard';
    },
  });

  // ── Screens ────────────────────────────────────────────────────────────────
  function showStep0(): void {
    const s = mountStep0({ root: root!, content: catalog.step0, onStart: enterFromIntro });
    if (document.readyState === 'complete') s.ready();
    else window.addEventListener('load', () => s.ready(), { once: true });
  }

  // After the intro: show saved designs if any exist, else go straight to base picker.
  async function enterFromIntro(): Promise<void> {
    const favs = await listFavorites();
    if (favs.length) showRecent(favs, true);
    else showStep1(true);
  }

  function showRecent(favorites: SavedBuild[], animated = false): void {
    swap(() => mountRecentCreations({
      root: root!, catalog, favorites,
      onNewDesign: () => { editingFavoriteId = null; editCartGroupId = null; build = createBuild('bracelet'); showStep1(true); },
      onEdit: openFavorite,
      onAddToBag: fav => { const { build: b } = hydrateBuild(catalog, fav.build); return addBuildToCart(catalog, b); },
      onDelete: id => removeFavorite(id),
    }), animated);
  }

  function showStep1(animated = false): void {
    swap(() => mountStep1({ root: root!, catalog, intro: catalog.step1, onChoose }), animated);
  }

  // Step 2 (design) + step 3 (review) share one mount: review is an in-place morph.
  function showStep2(animated = false): void {
    swap(() => mountConfigurator(root!, catalog, build, {
      onBack: () => { void leaveEditor(() => showStep1(true)); },
      onSave: () => saveCurrent(), // returns the promise so the review Save button can show its loading state
      updateCartGroupId: editCartGroupId,
      onAddedToBag: () => {
        bagSig = buildSignature(build);
        // Updated a cart piece → its old build payload is no longer needed.
        if (editCartGroupId) { try { localStorage.removeItem(cartBuildKey(editCartGroupId)); } catch { /* ignore */ } editCartGroupId = null; }
      },
    }), animated);
  }

  // Load a saved design into the editor (limbo: the stored record stays untouched
  // until an explicit save). Drops charms that are gone/out of stock and says so.
  function openFavorite(fav: SavedBuild): void {
    const { build: hydrated, dropped } = hydrateBuild(catalog, fav.build);
    build = hydrated;
    editingFavoriteId = fav.id;
    editCartGroupId = null; // a favorite, not a cart piece
    baselineSig = buildSignature(hydrated); // "clean as loaded" — auto-drops don't nag
    bagSig = '';
    showStep2(true);
    if (dropped > 0) toast(`${dropped} charm${dropped === 1 ? '' : 's'} no longer available — removed`);
  }

  function onChoose(base: BaseKind): void {
    // Picking a base wipes charms → this is a fresh design, not an edit of a favorite/cart.
    editingFavoriteId = null;
    editCartGroupId = null;
    setBase(build, base);                       // also sets the max-charm limit (BASE_CONFIG)
    // A PDP deep-link (?use=<chain>) preselects that chain when the chosen base supports it
    // (in stock); otherwise fall back to the default. Beads bases use palettes → falls back.
    const wantedChain = pendingChainId
      ? catalog.chains.find(c => c.id === pendingChainId && c.variants.some(v => v.base === base && v.inventory > 0)) ?? null
      : null;
    // Default to the first IN-STOCK chain for this base (fall back to any base chain, then any).
    const chain = wantedChain ??
      catalog.chains.find(c => c.variants.some(v => v.base === base && v.inventory > 0)) ??
      catalog.chains.find(c => c.variants.some(v => v.base === base)) ??
      catalog.chains[0] ?? null;
    setChain(build, chain);
    // Default to the first in-stock closure available for this chain (metal-matched).
    const closure = closuresForChain(catalog, chain).find(c => c.available > 0) ?? null;
    setClosure(build, closure ? toClosureSelection(closure) : null);
    setLength(build, defaultLength(base, lengthBounds(chain, base)));
    build.charms = [];                          // fresh design for the chosen base
    // A PDP deep-link (?use=<charm>) drops that charm into the tray (unplaced); it re-appears
    // on mount via the build.charms rebuild in mountConfigurator.
    if (pendingCharmId) {
      const item = catalog.charms.find(c => c.productId === pendingCharmId);
      if (item && item.available > 0) addCharm(build, toCharmProduct(item), null);
    }
    pendingCharmId = null;                       // one-shot: consumed on the first base pick
    pendingChainId = null;
    baselineSig = buildSignature(build);        // baseline = the empty starting build
    bagSig = '';
    showStep2(true);
  }

  // Arrived from the cart's checkout stock-check ("Edit my custom piece"): reopen that
  // build straight in step 2 (the line was already removed from the cart by the guard).
  function openFromCart(groupId: string): boolean {
    try {
      const raw = localStorage.getItem(cartBuildKey(groupId));
      if (!raw) return false;
      const { build: hydrated } = hydrateBuild(catalog, JSON.parse(raw));
      build = hydrated;
      editingFavoriteId = null;
      editCartGroupId = groupId;              // re-adding will UPDATE this cart piece
      baselineSig = buildSignature(hydrated);
      bagSig = '';
      history.replaceState(null, '', location.pathname); // drop ?edit= so a refresh starts fresh
      showStep2(false);
      return true;
    } catch {
      return false;
    }
  }

  console.log('[configurator] hydrated', {
    chains: catalog.chains.length,
    charms: catalog.charms.length,
    bases: catalog.bases.map(b => b.kind),
  });

  const editId = new URLSearchParams(location.search).get('edit');
  if (editId && openFromCart(editId)) return; // skip the intro flow

  // PDP deep-link (?use=<productId>): preload a charm/chain and drop the shopper straight into
  // step 1 (base picker), skipping the intro. The item is applied once a base is chosen (onChoose).
  const useId = new URLSearchParams(location.search).get('use');
  if (useId) {
    const gid = /^gid:\/\//.test(useId) ? useId : `gid://shopify/Product/${useId}`;
    if (catalog.charms.some(c => c.productId === gid)) pendingCharmId = gid;
    else if (catalog.chains.some(c => c.id === gid)) pendingChainId = gid;
    if (pendingCharmId || pendingChainId) {
      history.replaceState(null, '', location.pathname); // drop ?use= so a refresh starts fresh
      // Mount step 1 directly and fade it in explicitly. There's no prior screen to cross-fade
      // from at boot (only the <noscript> placeholder), so we don't route through swap() — we
      // clear the root and run the opacity fade ourselves with a forced reflow so it always runs.
      root.innerHTML = '';
      mountStep1({ root, catalog, intro: catalog.step1, onChoose });
      if (!REDUCE) {
        // Web Animations API: plays immediately on the freshly-mounted element. A CSS opacity
        // transition needs a painted baseline frame, which the very first paint after a full-page
        // navigation doesn't reliably provide (so reflow/double-rAF both no-op'd here); animate()
        // doesn't depend on that, so the fade always runs.
        root.firstElementChild?.animate(
          [{ opacity: 0 }, { opacity: 1 }],
          { duration: FADE_MS, easing: 'ease' },
        );
      }
      return;
    }
  }

  showStep0();
}

// `defer` guarantees the DOM (incl. #configurator-data) is parsed first, but
// guard anyway in case the script is loaded some other way.
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
else init();
